import { FormEvent, useState } from 'react'
import {
  AlertIcon,
  CurecallButton,
  CurecallTextarea,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { TPatient } from 'src/types/patient'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { PatientService } from 'src/services/patient.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPatientFollowup } from 'src/types/followup'
import { getFullName } from 'src/utils/string-formatter.util'
import { useSession } from 'src/providers/auth.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { AxiosError } from 'axios'

type TSendSMSProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  currentPatientFollowup: Pick<TPatientFollowup, 'id' | 'FollowupId'>
}

const SendSMS = ({ patient, currentPatientFollowup }: TSendSMSProps) => {
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const { t } = useTranslation()
  const { currentUser } = useSession()

  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const isUserRole = currentUser?.role === 'user'
  const maxLength = isUserRole ? 306 : undefined

  const handlePostSms = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    setErrorMessage('')
    try {
      await PatientService.postSMS(project.id, patient.id, {
        message,
        PatientFollowupId: currentPatientFollowup.id,
      })
      setMessage('')
      notify({
        title: t('components.sms.sms-sent'),
        message: t('components.sms.message-sent-to', { name: getFullName(patient) }),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        const backendErrorMessage = getErrorMessage(error)
        console.log(backendErrorMessage)
        if (backendErrorMessage === 'Message too long.') {
          setErrorMessage(t('components.sms.error-too-long'))
        } else {
          setErrorMessage(t('components.sms.error-impossible-sending'))
        }
      } else {
        notify({
          title: t('common.error.axios'),
          message: t('components.sms.error-on-sending'),
          appearance: ECurecallNotificationAppearance.ERROR,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handlePostSms} className="c-w-full c-flex c-flex-col">
      <div className="c-flex c-items-end c-gap-4">
        <div className="tw-w-full">
          <CurecallTextarea
            placeholder={t('components.sms.type-your-message')}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value)
              setErrorMessage('')
            }}
            label={t('components.sms.send-a-sms')}
            wrapperClassName="tw-w-full"
            textareaClassName="tw-h-16"
            labelClassName="tw-font-semibold"
            maxLength={maxLength}
            errorMessage={errorMessage}
          />
        </div>

        <CurecallButton
          appearance={ECurecallButtonAppearance.PRIMARY}
          type="submit"
          disabled={!message}
          isLoading={isLoading}
          className="c-ml-auto c-text-nowrap c-mb-4"
        >
          {t('components.sms.send-the-sms')}
        </CurecallButton>
      </div>
      <p className="c-mt-6 c-font-nexa c-text-orange-800">
        <AlertIcon className="c-h-4 c-inline-block c-mr-2 c-text-orange-800" />
        {t('components.sms.message-is-sent-instantly')}
      </p>
    </form>
  )
}

export default SendSMS
